// Live markdown rendering (remark/rehype + react-markdown) with the wiki-link
// resolver and attachment handling: images inline via presigned GET, other
// attachments as download links (web-viewer spec §Behavior). Internal note
// links route through react-router; unresolved wiki links stay inert text.
//
// Security invariant (the CSP threat model in decisions/88 depends on it): raw
// HTML is NEVER rendered. react-markdown's default rehype pipeline drops raw
// HTML nodes (we deliberately do not add rehype-raw), and rehype-highlight only
// tokenizes code text — so a note containing <script>/<img onerror=…>/inline
// styles renders as inert text, never live DOM.

import {
  useEffect,
  useState,
  type AnchorHTMLAttributes,
  type ImgHTMLAttributes,
  type InputHTMLAttributes,
} from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeHighlight from "rehype-highlight";
import { Link } from "react-router-dom";
import { IconExternalLink } from "@tabler/icons-react";
import type { FileEntry } from "@cairn/core";
import { attachmentUrl, type ApiClient } from "../api";
import {
  ATTACHMENT_SCHEME,
  fileRoute,
  parseAttachmentRef,
  remarkWikiLinks,
  resolveRelativeLink,
  resolveWikiTarget,
} from "../wikiLinks";
import { Checkbox } from "@/components/ui/checkbox";

export interface MarkdownProps {
  text: string;
  vaultId: string;
  entries: FileEntry[];
  api: ApiClient;
  /** Vault-relative path of the note being rendered. Standard markdown links
   *  resolve relative to this note's folder (Obsidian semantics); optional and
   *  defensive — when absent, relative links resolve from the vault root. */
  sourcePath?: string;
}

/** Keep our internal attachment scheme; sanitize everything else as usual. */
function urlTransform(url: string): string {
  if (url.startsWith(ATTACHMENT_SCHEME)) return url;
  return defaultUrlTransform(url);
}

/** A pure in-page anchor (`#heading`): stays a same-tab link, never routed. */
function isInPageAnchor(href: string): boolean {
  return href.startsWith("#");
}

/** An off-vault absolute URL: any URL scheme (`http:`, `https:`, `mailto:`,
 *  `tel:`, …) or a protocol-relative `//host`. These open in a new tab with an
 *  external affordance; they are never resolved through the meta path map. */
function isExternalUrl(href: string): boolean {
  return href.startsWith("//") || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href);
}

function AttachmentImg({
  hash,
  alt,
  vaultId,
  api,
}: {
  hash: string;
  alt: string;
  vaultId: string;
  api: ApiClient;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    attachmentUrl(api, vaultId, hash)
      .then((u) => {
        if (!cancelled) setSrc(u);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api, vaultId, hash]);
  if (failed) return <span className="attachment-missing">[missing attachment: {alt}]</span>;
  if (!src) return <span className="attachment-loading">{alt}</span>;
  return <img src={src} alt={alt} className="md-image" />;
}

export function Markdown({ text, vaultId, entries, api, sourcePath }: MarkdownProps) {
  const components = {
    a: (props: AnchorHTMLAttributes<HTMLAnchorElement>) => {
      const { href, children, ...rest } = props;
      if (href === undefined) return <a {...rest}>{children}</a>;
      // Non-image attachment → presign on click, then open (download link).
      const downloadAnchor = (hash: string) => (
        <a
          {...rest}
          href="#download"
          className="wiki-link wiki-attachment"
          onClick={(e) => {
            e.preventDefault();
            void attachmentUrl(api, vaultId, hash).then((u) => {
              window.open(u, "_blank", "noopener");
            });
          }}
        >
          {children}
        </a>
      );
      const ref = parseAttachmentRef(href);
      if (ref) return downloadAnchor(ref.hash);
      // Already-resolved internal route (e.g. from remarkWikiLinks) → SPA nav.
      if (href.startsWith("/v/")) {
        return (
          <Link to={href} className={props.className ?? "wiki-link"}>
            {children}
          </Link>
        );
      }
      // Pure in-page anchor: keep it a same-tab link, no external affordance.
      if (isInPageAnchor(href)) {
        return (
          <a {...rest} href={href}>
            {children}
          </a>
        );
      }
      // Genuinely external absolute URL → new tab, with an external indicator.
      if (isExternalUrl(href)) {
        return (
          <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
            {children}
            <IconExternalLink
              className="external-link ml-0.5 inline-block size-3 align-baseline"
              aria-hidden
            />
            <span className="sr-only"> (opens in new tab)</span>
          </a>
        );
      }
      // Otherwise a standard vault-relative markdown link, e.g.
      // `[x](../folder/My%20Note.md)`. Obsidian resolves these against the
      // SOURCE note's folder (unlike vault-global wiki-links), so it navigates
      // in-app instead of opening a 404'ing new tab. resolveRelativeLink also
      // guards the %-decode: a malformed encoding leaves the link inert rather
      // than throwing and aborting the whole note render.
      const hashIdx = href.indexOf("#");
      const fragment = hashIdx >= 0 ? href.slice(hashIdx) : "";
      const entry = resolveRelativeLink(href, sourcePath, entries);
      if (entry) {
        if (entry.kind === "attachment") {
          if (entry.hash !== undefined) return downloadAnchor(entry.hash);
        } else {
          return (
            <Link
              to={fileRoute(vaultId, entry.path) + fragment}
              className={props.className ?? "wiki-link"}
            >
              {children}
            </Link>
          );
        }
      }
      // Unresolved in-vault link → inert text, never a dead new-tab 404,
      // matching how unresolved wiki-links stay plain text.
      return <span>{children}</span>;
    },
    img: (props: ImgHTMLAttributes<HTMLImageElement>) => {
      const { src, alt } = props;
      if (typeof src !== "string" || src === "") return null;
      const ref = parseAttachmentRef(src);
      if (ref) {
        return <AttachmentImg hash={ref.hash} alt={alt ?? ref.name} vaultId={vaultId} api={api} />;
      }
      // Standard markdown image with a vault-relative path: resolve through
      // the meta path map like a wiki target.
      const entry = resolveWikiTarget(decodeURIComponent(src), entries);
      if (entry?.kind === "attachment" && entry.hash !== undefined) {
        return (
          <AttachmentImg hash={entry.hash} alt={alt ?? entry.path} vaultId={vaultId} api={api} />
        );
      }
      // External or unresolvable image: render as-is; the strict CSP decides.
      return <img src={src} alt={alt ?? ""} className="md-image" />;
    },
    // GFM task-list checkboxes → a read-only shadcn Checkbox (content is
    // read-only in the viewer). Everything else stays a normal input.
    input: (props: InputHTMLAttributes<HTMLInputElement>) => {
      if (props.type === "checkbox") {
        return <Checkbox checked={!!props.checked} disabled className="task-checkbox" />;
      }
      return <input {...props} />;
    },
  };

  return (
    <div
      className={
        "markdown prose prose-neutral dark:prose-invert min-w-0 max-w-3xl flex-1 " +
        "prose-code:before:content-none prose-code:after:content-none " +
        "prose-pre:bg-transparent prose-pre:p-0"
      }
    >
      <ReactMarkdown
        // remark-breaks turns a single source newline into a hard <br>, matching
        // Obsidian's default (CommonMark would collapse it to a space). It runs
        // AFTER remark-gfm (so GFM block/table parsing is untouched) and BEFORE
        // remarkWikiLinks; wiki-links never span a newline (WIKI_RE excludes
        // "\n"), so the break nodes it inserts don't affect wiki resolution, and
        // relative-link resolution happens later in the <a> renderer regardless.
        remarkPlugins={[remarkGfm, remarkBreaks, [remarkWikiLinks, { vaultId, entries }]]}
        rehypePlugins={[[rehypeHighlight, { detect: true, ignoreMissing: true }]]}
        urlTransform={urlTransform}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
